import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Textarea } from 'src/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  canAddDirectChildToArrayField,
  getArrayItemTypeSelection,
  getVisibleArrayChildren,
  isArrayItemPlaceholderName,
  isContainerFieldType,
  setArrayItemTypeSelection,
} from '@erp/shared-utils';
import {
  FRIENDLY_FIELD_TYPES,
  completeRow,
  entityReferenceOptions,
  isEntityReferenceTarget,
  type FriendlyFieldRow,
  type FriendlyFieldType,
} from './quickSchemaFieldTree';
import { DefaultValueInput, initialDefaultForType } from './DefaultValueInput';
import { ArrayItemTypeSelect } from './ArrayItemTypeSelect';

function schemaFieldTypeLabel(
  fieldType: string,
  gt: (text: string, options: { context: string }) => string,
): string {
  if (fieldType === 'entity_reference') {
    return gt('Entity', {
      context:
        'schema field type for a reference to a system entity such as a supplier or customer',
    });
  }
  if (fieldType === 'document') {
    return gt('Document', {
      context: 'schema field type for a reference to an uploaded document',
    });
  }
  return fieldType;
}

type QuickSchemaFieldRowProps = {
  row: FriendlyFieldRow;
  depth: number;
  formId: string;
  saving: boolean;
  isRoot: boolean;
  isKey: boolean;
  onFieldNameChange: (rowId: string, oldName: string, newName: string) => void;
  onUpdate: (
    rowId: string,
    updater: (row: FriendlyFieldRow) => FriendlyFieldRow,
  ) => void;
  onRemove: (rowId: string) => void;
  onAddChild: (parentId: string) => void;
  onKeyCheck?: (rowId: string, checked: boolean) => void;
  /** When 'extraction', show the per-field Extraction Hints control. */
  schemaType?: string;
  /** Type-select options (defaults to FRIENDLY_FIELD_TYPES; edit passes the field's current type too). */
  fieldTypeOptions?: readonly string[];
  /** Header +Child / Trash buttons. Off when editing a single existing field (the tree owns structure). */
  showActions?: boolean;
  /** Recursive child rows. Off when editing a single existing field. */
  renderChildren?: boolean;
};

export function QuickSchemaFieldRow({
  row,
  depth,
  formId,
  saving,
  isRoot,
  isKey,
  onFieldNameChange,
  onUpdate,
  onRemove,
  onAddChild,
  onKeyCheck,
  schemaType,
  fieldTypeOptions = FRIENDLY_FIELD_TYPES,
  showActions = true,
  renderChildren = true,
}: QuickSchemaFieldRowProps) {
  const gt = useGT();
  const canHaveChildren =
    row.fieldType === 'object' ||
    (row.fieldType === 'array' &&
      (canAddDirectChildToArrayField(row) || row.arrayItemMode === 'object'));
  const addChildLabel =
    row.fieldType === 'array' ? (
      <T context="button to add a property on array object items in quick create">
        + Property
      </T>
    ) : (
      <T context="button to add a child field in quick create">+ Child</T>
    );
  const visibleChildren =
    row.fieldType === 'array' ? getVisibleArrayChildren(row) : row.children;
  const arrayItemSelection =
    row.fieldType === 'array'
      ? row.arrayItemMode === 'object' && row.children.length === 0
        ? 'object'
        : getArrayItemTypeSelection(row)
      : null;
  // Default values apply to non-container scalar fields; an explicit "Has
  // default" checkbox gates whether one is set (so a string column can default
  // to an empty string, while numeric/temporal types require a real value).
  const showDefaultValueControl =
    !isContainerFieldType(row.fieldType) &&
    row.fieldType !== 'entity_reference' &&
    row.fieldType !== 'document' &&
    !isArrayItemPlaceholderName(row.fieldName);
  const showExtractionHints =
    schemaType === 'extraction' && !isArrayItemPlaceholderName(row.fieldName);
  // The "Has default" toggle sits on the same row as the value input (to its
  // right); the input is greyed out until it is enabled.
  const hasDefaultCheckbox = showDefaultValueControl ? (
    <div className="flex items-center gap-2 pb-1">
      <Checkbox
        id={`${formId}-hasdefault-${row.id}`}
        checked={row.hasDefault}
        onCheckedChange={(checked) =>
          onUpdate(row.id, (current) => ({
            ...current,
            hasDefault: checked === true,
            // Enabling seeds a type-appropriate starting value ('' for string
            // types, null for numeric/temporal/JSON); disabling clears it.
            defaultValue:
              checked === true
                ? (current.defaultValue ??
                  initialDefaultForType(current.fieldType))
                : null,
          }))
        }
        disabled={saving}
      />
      <Label
        htmlFor={`${formId}-hasdefault-${row.id}`}
        className="whitespace-nowrap text-xs font-normal"
      >
        <T context="checkbox to enable a column default value">Has default</T>
      </Label>
    </div>
  ) : null;

  const defaultValueField = showDefaultValueControl ? (
    <div className="w-full space-y-1">
      <Label className="text-[10px] text-muted-foreground">
        <T context="default value label quick schema">Default Value</T>
      </Label>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <DefaultValueInput
            // Remount on type/enabled change so the input resets to the value.
            key={`${row.fieldType}-${row.hasDefault}`}
            fieldType={row.fieldType}
            value={row.defaultValue}
            onChange={(next) =>
              onUpdate(row.id, (current) => ({
                ...current,
                defaultValue: next,
              }))
            }
            disabled={saving || !row.hasDefault}
            placeholder={
              row.hasDefault
                ? undefined
                : gt('No default value', {
                    context: 'placeholder when default value is disabled',
                  })
            }
            compact
          />
        </div>
        {hasDefaultCheckbox}
      </div>
    </div>
  ) : null;

  const requiredControl = (
    <div className="flex items-center gap-2 pb-1">
      <Checkbox
        id={`${formId}-req-${row.id}`}
        checked={row.isRequired}
        onCheckedChange={(checked) =>
          onUpdate(row.id, (current) =>
            isKey ? current : { ...current, isRequired: checked === true },
          )
        }
        disabled={saving || isKey}
      />
      <Label
        htmlFor={`${formId}-req-${row.id}`}
        className="text-xs font-normal"
      >
        <T context="required checkbox quick schema">Required</T>
      </Label>
    </div>
  );

  return (
    <div className="space-y-2">
      <div
        className="rounded-md border bg-card p-2"
        style={{ marginLeft: depth > 0 ? depth * 16 : 0 }}
      >
        {showActions ? (
          <div className="mb-2 flex items-center justify-end gap-2">
            <div className="flex items-center gap-1">
              {canHaveChildren && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onAddChild(row.id)}
                  disabled={saving}
                >
                  <PlusIcon className="mr-1 h-3 w-3" aria-hidden />
                  {addChildLabel}
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                aria-label={gt('Remove field', {
                  context: 'aria remove field quick schema',
                })}
                onClick={() => onRemove(row.id)}
                disabled={saving}
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-end gap-2">
          {!isArrayItemPlaceholderName(row.fieldName) ? (
            <div className="min-w-[7rem] flex-1 space-y-1">
              <Label className="text-[10px] text-muted-foreground">
                <T context="column field name quick schema">Field</T>
              </Label>
              <Input
                value={row.fieldName}
                onChange={(e) =>
                  onFieldNameChange(row.id, row.fieldName, e.target.value)
                }
                placeholder={
                  isRoot
                    ? gt('order_id', {
                        context: 'placeholder field key quick schema',
                      })
                    : gt('property_name', {
                        context: 'placeholder nested field key quick schema',
                      })
                }
                className="h-8 font-mono text-xs"
                disabled={saving}
              />
            </div>
          ) : null}
          <div className="w-[8.5rem] space-y-1">
            <Label className="text-[10px] text-muted-foreground">
              <T context="column type quick schema">Type</T>
            </Label>
            <Select
              value={row.fieldType}
              onValueChange={(v) =>
                onUpdate(row.id, (current) => ({
                  ...current,
                  fieldType: v as FriendlyFieldType,
                  // Reset the default — it is type-specific.
                  hasDefault: false,
                  defaultValue: null,
                  referencedEntityType:
                    v === 'entity_reference'
                      ? (current.referencedEntityType ?? null)
                      : null,
                  children: isContainerFieldType(v as FriendlyFieldType)
                    ? current.children
                    : [],
                }))
              }
              disabled={saving}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fieldTypeOptions.map((t) => (
                  <SelectItem key={t} value={t}>
                    {schemaFieldTypeLabel(t, gt)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {row.fieldType === 'entity_reference' ? (
            <div className="w-[11rem] space-y-1">
              <Label
                htmlFor={`${formId}-entity-${row.id}`}
                className="text-[10px] text-muted-foreground"
              >
                <T context="label for which system entity a schema field references">
                  Entity
                </T>
              </Label>
              <Select
                value={row.referencedEntityType ?? ''}
                onValueChange={(entity) => {
                  if (!isEntityReferenceTarget(entity)) return;
                  onUpdate(row.id, (current) => ({
                    ...current,
                    referencedEntityType: entity,
                  }));
                }}
                disabled={saving}
              >
                <SelectTrigger
                  id={`${formId}-entity-${row.id}`}
                  className="h-8 text-xs"
                >
                  <SelectValue
                    placeholder={gt('Select entity', {
                      context:
                        'placeholder for the system entity a schema field references',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {entityReferenceOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {isKey ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>{requiredControl}</TooltipTrigger>
                <TooltipContent>
                  <T context="tooltip explaining required is locked for key fields">
                    Key fields are always required.
                  </T>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            requiredControl
          )}
          {isRoot && onKeyCheck ? (
            <div className="flex items-center gap-2 pb-1">
              <Checkbox
                id={`${formId}-key-${row.id}`}
                checked={isKey}
                onCheckedChange={(checked) =>
                  onKeyCheck(row.id, checked === true)
                }
                disabled={saving}
              />
              <Label
                htmlFor={`${formId}-key-${row.id}`}
                className="text-xs font-normal"
              >
                <T context="key checkbox quick schema">Key</T>
              </Label>
            </div>
          ) : null}
          {defaultValueField}
          <div className="w-full space-y-1">
            {!isArrayItemPlaceholderName(row.fieldName) ? (
              <>
                <Label className="text-[10px] text-muted-foreground">
                  <T context="column description quick schema">Description</T>
                </Label>
                <Input
                  value={row.description}
                  onChange={(e) =>
                    onUpdate(row.id, (current) => ({
                      ...current,
                      description: e.target.value,
                    }))
                  }
                  placeholder={gt(
                    'Optional — also used as the AI extraction hint',
                    {
                      context: 'placeholder field description quick schema',
                    },
                  )}
                  className="h-8 text-xs"
                  disabled={saving}
                />
              </>
            ) : null}
          </div>
          {showExtractionHints ? (
            <div className="w-full">
              <Collapsible>
                <CollapsibleTrigger className="text-[10px] font-medium text-muted-foreground hover:text-foreground">
                  <T context="extraction hints collapsible quick schema">
                    Extraction Hints
                  </T>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-1">
                  <Textarea
                    value={row.extractionHints?.aiPromptHint ?? ''}
                    onChange={(e) =>
                      onUpdate(row.id, (current) => ({
                        ...current,
                        extractionHints: e.target.value
                          ? { aiPromptHint: e.target.value }
                          : null,
                      }))
                    }
                    placeholder={gt(
                      'Hint for the AI model on how to extract this field',
                    )}
                    rows={2}
                    className="text-xs"
                    disabled={saving}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>
          ) : null}
        </div>

        {row.fieldType === 'array' && arrayItemSelection ? (
          <div className="mt-2">
            <ArrayItemTypeSelect
              id={`${formId}-array-item-${row.id}`}
              value={arrayItemSelection}
              disabled={saving}
              onChange={(selection) =>
                onUpdate(row.id, (current) => {
                  if (selection === 'object') {
                    return {
                      ...current,
                      arrayItemMode: 'object',
                      children: current.children,
                    };
                  }
                  const updated = setArrayItemTypeSelection(current, selection);
                  return {
                    ...current,
                    children: (updated.children ?? []).map(completeRow),
                    arrayItemMode: undefined,
                  };
                })
              }
            />
          </div>
        ) : null}
      </div>

      {renderChildren
        ? visibleChildren.map((child) => (
            <QuickSchemaFieldRow
              key={child.id}
              row={child}
              depth={depth + 1}
              formId={formId}
              saving={saving}
              isRoot={false}
              isKey={false}
              onFieldNameChange={onFieldNameChange}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onAddChild={onAddChild}
              schemaType={schemaType}
            />
          ))
        : null}
    </div>
  );
}
